import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFLogo from '@/components/pdf/PDFLogo';

export interface Definition {
  title: string;
  text: string;
}

export interface BarChartItem {
  label: string;
  value: number;
}

export interface AssessmentData {
  score: number;
  definitions: Definition[];
  barChartData: BarChartItem[];
}

interface AssessmentPDFDocumentProps {
  data: AssessmentData;
}

const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    padding: 0,
    paddingTop: 50,
    paddingBottom: 40,
    fontFamily: 'Helvetica',
  },
  headerBar: {
    position: 'absolute',
    top: 10,
    left: 20,
    right: 20,
    height: 30,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  logo: {
    width: 80,
    height: 20,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 8,
    color: '#6b7280',
  },
  sidebar: {
    width: '55%',
    backgroundColor: '#5E9E2E',
    padding: 20,
    color: 'white',
  },
  content: {
    width: '45%',
    padding: 20,
    backgroundColor: 'white',
  },
  domainTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 30,
    color: '#1f2937',
    backgroundColor: 'white',
    padding: 10,
    marginLeft: -20,
    marginRight: -20,
    marginTop: -20,
  },
  subdomainHeader: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  scoreCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2pt solid white',
  },
  scoreText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  definitionItem: {
    marginBottom: 15,
    paddingLeft: 15,
  },
  definitionNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  definitionText: {
    fontSize: 9,
    lineHeight: 1.3,
    textAlign: 'justify',
  },
  chartSection: {
    marginTop: 20,
  },
  chartTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  chartContainer: {
    height: 200,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 10,
    marginBottom: 20,
  },
  barChart: {
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  barLabel: {
    width: 80,
    fontSize: 8,
    color: '#374151',
  },
  barContainer: {
    flex: 1,
    height: 12,
    backgroundColor: '#e5e7eb',
    marginLeft: 10,
    marginRight: 10,
    borderRadius: 6,
  },
  barFill: {
    height: '100%',
    backgroundColor: '#5E9E2E',
    borderRadius: 6,
  },
  barValue: {
    fontSize: 8,
    color: '#374151',
    width: 30,
  },
  textBox: {
    height: 150,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 10,
    marginTop: 10,
  },
  textBoxHeader: {
    fontSize: 10,
    textAlign: 'center',
    marginBottom: 10,
    backgroundColor: '#dbeafe',
    padding: 5,
    borderRadius: 4,
  },
  textBoxContent: {
    fontSize: 8,
    lineHeight: 1.3,
    textAlign: 'justify',
  },
});

export const AssessmentPDFDocument: React.FC<AssessmentPDFDocumentProps> = ({ data }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page} orientation="landscape">
      <View style={pdfStyles.headerBar}>
        <PDFLogo width={80} height={20} />
      </View>
      <View style={pdfStyles.sidebar}>
        <Text style={pdfStyles.domainTitle}>Organizational Infrastructure Domain</Text>
        
        <View style={pdfStyles.subdomainHeader}>
          <Text>Subdomain Definition</Text>
          <View style={pdfStyles.scoreCircle}>
            <Text style={pdfStyles.scoreText}>{data.score}%</Text>
          </View>
        </View>

        {data.definitions.map((definition: Definition, index: number) => (
          <View key={index} style={pdfStyles.definitionItem}>
            <Text style={pdfStyles.definitionNumber}>{index + 1}. {definition.title}</Text>
            <Text style={pdfStyles.definitionText}>{definition.text}</Text>
          </View>
        ))}
      </View>

      <View style={pdfStyles.content}>
        <View style={pdfStyles.textBox}>
          <Text style={pdfStyles.textBoxContent}>
            On the other hand, we denounce with righteous indignation and dislike men who are so beguiled and 
            demoralized by the charms of pleasure of the moment, so blinded by desire, that they cannot foresee 
          </Text>
        </View>

        <View style={pdfStyles.chartSection}>
          <Text style={pdfStyles.chartTitle}>Performance Metrics</Text>
          <View style={pdfStyles.chartContainer}>
            <View style={pdfStyles.barChart}>
              {data.barChartData.map((item: BarChartItem, index: number) => (
                <View key={index} style={pdfStyles.barRow}>
                  <Text style={pdfStyles.barLabel}>{item.label}</Text>
                  <View style={pdfStyles.barContainer}>
                    <View style={[pdfStyles.barFill, { width: `${item.value}%` }]} />
                  </View>
                  <Text style={pdfStyles.barValue}>{item.value}%</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>
      <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
    </Page>
  </Document>
); 